import { motion } from "framer-motion";
import React, { useEffect, useState } from "react";
import emailjs from "@emailjs/browser";
import ReactCountryFlag from "react-country-flag";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";
import "./ContactUs.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const getAllCountries = () => {
  return getCountries().map((code) => ({
    code,
    dial: `+${getCountryCallingCode(code)}`,
    name: new Intl.DisplayNames(["en"], { type: "region" }).of(code),
  }));
};

const allCountries = getAllCountries();
const dropCountries = ["US", "GB", "IN", "CA"];
const initialCountries = dropCountries.map((code) =>
  allCountries.find((c) => c.code === code)
);

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.45 },
  },
};

const Contact = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [selectedCountry, setSelectedCountry] = useState(
    allCountries.find((c) => c.code === "PK")
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!dropdownOpen) setShowAllCountries(false);
  }, [dropdownOpen]);

  const displayCountries = showAllCountries ? allCountries : initialCountries;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isChecked) return;

    setLoading(true);

    const templateParams = {
      firstName,
      lastName,
      email,
      phone,
      country: selectedCountry.dial,
      message,
    };

    try {
      const res = await emailjs.send(
        process.env.REACT_APP_SERVICE_ID,
        process.env.REACT_APP_TEMPLATE_CONTACT_ID,
        templateParams,
        process.env.REACT_APP_API_KEY
      );

      if (res.status === 200) {
        toast.success("Your message has been sent successfully!", {
          position: "top-right",
          autoClose: 3000,
          theme: "dark",
        });

        setFirstName("");
        setLastName("");
        setEmail("");
        setPhone("");
        setMessage("");
        setIsChecked(false);
      }
    } catch (error) {
      console.error("EmailJS Error:", error);
      toast.error("Message failed to send. Please try again.", {
        position: "top-right",
        autoClose: 3000,
        theme: "dark",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contactPage">
      <motion.div
        className="contactContainer"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.p variants={itemVariants} className="contactSubheading">
          Contact us
        </motion.p>
        <motion.h1 variants={itemVariants} className="contactHeading">
          Get in touch
        </motion.h1>
        <motion.p variants={itemVariants} className="contactDescription">
          We'd love to hear from you. Please fill out this form.
        </motion.p>

        <form className="contactForm" onSubmit={handleSubmit}>
          <motion.div variants={itemVariants} className="formRow">
            <div className="form_Group">
              <label>First name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>
            <div className="form_Group">
              <label>Last name</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="form_Group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </motion.div>

          <motion.div variants={itemVariants} className="form_Group">
            <label>Phone number</label>
            <div className="phone_InputGroup">
              <div
                className="countrySelect"
                onClick={() => setDropdownOpen(!dropdownOpen)}
              >
                <ReactCountryFlag
                  countryCode={selectedCountry.code}
                  svg
                  style={{ width: "20px", height: "20px" }}
                />
                <span>
                  {selectedCountry.name} ({selectedCountry.dial})
                </span>
              </div>

              {dropdownOpen && (
                <div className="countryDropdown">
                  <ul>
                    {displayCountries.map((country) => (
                      <li
                        key={country.code}
                        onClick={() => {
                          setSelectedCountry(country);
                          setDropdownOpen(false);
                        }}
                      >
                        <ReactCountryFlag
                          countryCode={country.code}
                          svg
                          style={{ width: "20px", height: "20px" }}
                        />
                        <span>
                          {country.name} ({country.dial})
                        </span>
                      </li>
                    ))}
                  </ul>
                  {!showAllCountries && (
                    <button
                      type="button"
                      className="moreCountriesBtn"
                      onClick={() => setShowAllCountries(true)}
                    >
                      Show all countries
                    </button>
                  )}
                </div>
              )}
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                style={{borderRadius:"8px"}}
              />
            </div>
          </motion.div>
          {/* boioi */}

          <motion.div variants={itemVariants} className="form_Group">
            <label>Message</label>
            <textarea
              rows="4"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
            />
          </motion.div>

          <motion.div variants={itemVariants} className="privacyWrapper">
            <label>Privacy Policy</label>
            <div className="privacyInfo">
              <ul className="privacyList">
                <li>
                  Your information is used only for communication and support
                  purposes.
                </li>
                <li>
                  We never sell, rent, or trade your personal data to third
                  parties.
                </li>
                <li>
                  Data is stored securely using industry-standard protection
                  methods.
                </li>
                <li>
                  You may be contacted via email or phone regarding your
                  inquiry.
                </li>
                <li>
                  Your information is handled in compliance with applicable data
                  laws.
                </li>
              </ul>
            </div>

            <div className="formCheckbox">
              <input
                type="checkbox"
                checked={isChecked}
                onChange={(e) => setIsChecked(e.target.checked)}
              />
              <span className="privacyText">I agree to the Privacy Policy</span>
            </div>
          </motion.div>

          <motion.div variants={itemVariants}>
            <button
              type="submit"
              className="submitButton"
              disabled={!isChecked || loading}
            >
              {loading ? "Sending..." : "Send message"}
            </button>
          </motion.div>
        </form>
      </motion.div>
    </div>
  );
};

export default Contact;
